import { cn, RankBadge } from "@tcg/ui";
import { Clock, PlayCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { DocumentButton } from "@/components/DocumentButton";
import { MoneyText, useYen } from "@/components/MoneyText";
import { call } from "@/lib/api";
import type { Hold } from "@/lib/types";

export type HoldTone = "ok" | "soon" | "expired";

const SOON_MS = 30 * 60_000;

/** Time left → colour: plenty (green), under 30 minutes (yellow), gone (期限切れ, red). */
export function holdTone(hold: Pick<Hold, "status" | "expiresAt">, now: number): HoldTone {
  if (hold.status === "expired") {
    return "expired";
  }
  const left = new Date(hold.expiresAt).getTime() - now;
  if (left <= 0) {
    return "expired";
  }
  return left <= SOON_MS ? "soon" : "ok";
}

function leftText(
  expiresAt: string,
  now: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const minutes = Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 60_000));
  if (minutes >= 60 * 24) {
    return t("holds.left_days", { count: Math.floor(minutes / (60 * 24)) });
  }
  if (minutes >= 60) {
    return t("holds.left_hours", { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
  }
  return t("holds.left_minutes", { count: minutes });
}

const TONE = {
  ok: "border-pos-ok/50",
  soon: "border-status-pending bg-status-pending/10",
  expired: "border-status-problem border-dashed",
} as const;

/** One 保留 / 取り置き (T07-28): who, how many, the total, the time left in colour, 呼び出す / 取消. */
export function HoldCard({
  hold,
  now,
  busy,
  onResume,
  onCancel,
}: {
  hold: Hold;
  now: number;
  busy: boolean;
  onResume: () => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const yen = useYen();
  const tone = holdTone(hold, now);
  const lang = i18n.resolvedLanguage === "ja" ? "ja" : "en";
  const items = hold.lines.reduce((count, line) => count + line.qty, 0);
  return (
    <li
      data-testid="hold-card"
      data-tone={tone}
      className={cn("flex flex-col gap-2 rounded-lg border-2 p-4", TONE[tone])}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-lg font-semibold">
            {hold.kind === "layaway" ? t("holds.kind_layaway") : t("holds.kind_park")}
            {" · "}
            {hold.customerName ?? t("holds.walk_in")}
          </p>
          <p className="text-sm text-muted-foreground">{t("holds.items", { count: items })}</p>
        </div>
        <MoneyText amount={hold.total} size="lg" />
      </div>
      <ul className="flex flex-col gap-0.5 text-sm">
        {hold.lines.slice(0, 3).map((line) => (
          <li key={line.lineId} className="flex items-center gap-2">
            {line.rawRank ? (
              <RankBadge rank={line.rawRank} label={t(`rank.${line.rawRank}`)} size="sm" />
            ) : null}
            <span className="truncate">{line.title[lang] ?? line.title.ja ?? line.title.en}</span>
            {line.qty > 1 ? <span>×{line.qty}</span> : null}
          </li>
        ))}
        {hold.lines.length > 3 ? (
          <li className="text-muted-foreground">
            {t("holds.more", { count: hold.lines.length - 3 })}
          </li>
        ) : null}
      </ul>
      {hold.note ? <p className="text-sm">📝 {hold.note}</p> : null}
      {hold.deposit ? (
        <p className="text-sm">{t("holds.deposit_line", { amount: yen(hold.deposit.amount) })}</p>
      ) : null}
      {hold.deposit ? <DepositReceiptButton hold={hold} /> : null}
      <p
        className={cn(
          "flex items-center gap-1 font-medium",
          tone === "expired" && "text-pos-problem",
        )}
      >
        <Clock aria-hidden className="size-4" />
        {tone === "expired" ? t("holds.expired") : leftText(hold.expiresAt, now, t)}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {tone === "expired" ? (
          <p className="col-span-1 self-center text-sm text-muted-foreground">
            {t("holds.expired_help")}
          </p>
        ) : (
          <button
            type="button"
            onClick={onResume}
            disabled={busy}
            className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md bg-primary text-lg font-semibold text-primary-foreground disabled:opacity-50"
          >
            <PlayCircle aria-hidden />
            {t("holds.resume")}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
        >
          <XCircle aria-hidden />
          {t("holds.cancel")}
        </button>
      </div>
    </li>
  );
}

/**
 * 預り証 (DP-…) of the deposit (T07-41/42): media-service's PDF — asked again while it is still
 * being made, opened for printing once it is. The card shows the new state by itself
 * (`hold.changed` / `pos.document.changed`).
 */
function DepositReceiptButton({ hold }: { hold: Hold }) {
  const { t } = useTranslation();
  const { api } = usePos();
  return (
    <DocumentButton
      label={t("document.deposit_receipt")}
      number={hold.depositReceipt?.number ?? null}
      state={hold.depositReceipt?.status ?? null}
      open={() =>
        call(() =>
          api.POST("/v1/pos/holds/{hold_id}/deposit-receipt", {
            params: { path: { hold_id: hold.id } },
          }),
        )
      }
    />
  );
}
